/* eslint-disable prefer-template */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { curveBasis, line } from 'd3-shape';

const MAX_DEPTH = 360;
const RIDGE_X_OFFSET = 2;
const RIDGE_Y_OFFSET = 9;
const LOCATION_NAMES = {
  spencersCreek: 'Spencers Creek',
  deepCreek: 'Deep Creek',
  threeMileDam: 'Three Mile Dam',
};
const MONTHS = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};
const MONTH_LABELS = Object.keys(MONTHS);

function parseDate(label) {
  const parts = label.split('-');
  return new Date(Number(parts[2]), MONTHS[parts[1]], Number(parts[0]));
}

function linePath(record, width, height, stopAtLastPoint, closeAtLastPoint) {
  const year = Number(record.year);
  const start = new Date(year, 0, 1).getTime();
  const end = new Date(year, 11, 31).getTime();
  const points = [[new Date(year, 0, 1), 0]].concat(
    record.dates.map((item) => [parseDate(item[0]), Number(item[1])]),
  );
  if (!stopAtLastPoint) points.push([new Date(year, 11, 31), 0]);
  else if (closeAtLastPoint && points.length > 1)
    points.push([points[points.length - 1][0], 0]);
  return line()
    .curve(curveBasis)
    .x((point) => ((point[0].getTime() - start) / (end - start)) * width)
    .y((point) => height - (point[1] / MAX_DEPTH) * height)(points);
}

function SnowLine({
  record,
  width,
  height,
  className,
  stopAtLastPoint,
  stroke,
}) {
  const d = useMemo(
    () => linePath(record, width, height, stopAtLastPoint),
    [record, width, height, stopAtLastPoint],
  );
  const area = linePath(record, width, height, stopAtLastPoint, true);
  return (
    <>
      <path className={className + ' area-fill'} d={area} />
      <path className={className} d={d} style={stroke ? { stroke } : null} />
    </>
  );
}

function YearRows({ data, activeYear, truncateYears }) {
  const width = 1000;
  const height = 120;
  const maxOffset = (data.length - 1) * RIDGE_X_OFFSET;
  const axisYear = Number(data[data.length - 1].year);
  const axisStart = new Date(axisYear, 0, 1).getTime();
  const axisEnd = new Date(axisYear, 11, 31).getTime();
  const monthMarkers = MONTH_LABELS.map((month, index) => {
    const monthStart = new Date(axisYear, index, 1).getTime();
    const nextMonth = Math.min(
      new Date(axisYear, index + 1, 1).getTime(),
      axisEnd,
    );
    return {
      month,
      left: ((monthStart - axisStart) / (axisEnd - axisStart)) * 100,
      width: ((nextMonth - monthStart) / (axisEnd - axisStart)) * 100,
    };
  });
  return (
    <div
      className="stacked-chart"
      aria-label="Seasonal snow depth by year"
      style={{ '--ridge-inset': `${maxOffset}px` }}
    >
      <div
        className="ridge-lines"
        style={{ height: data.length * RIDGE_Y_OFFSET + height }}
      >
        {data.map((record, index) => {
          const active = record.year === activeYear;
          return (
            <div
              className={
                'year-row' +
                (active ? ' is-active' : '') +
                (Number(record.year) % 10 === 0 ? ' is-major' : '') +
                (Number(record.year) <= 1980 ? ' is-left-label' : '')
              }
              key={record.year}
              style={{
                '--row': index,
                '--ridge-left': `${(data.length - index - 1) * RIDGE_X_OFFSET}px`,
                '--ridge-width-reduction': `${maxOffset}px`,
              }}
            >
              <span>{record.year}</span>
              <svg
                viewBox={'0 0 ' + width + ' ' + height}
                preserveAspectRatio="none"
                role="img"
                aria-label={record.year + ' snow depth'}
              >
                {[0.25, 0.5, 0.75].map((fraction) => (
                  <line
                    key={fraction}
                    x1="0"
                    x2={width}
                    y1={height * fraction}
                    y2={height * fraction}
                  />
                ))}
                <SnowLine
                  record={record}
                  width={width}
                  height={height}
                  className="year-row__line"
                  stopAtLastPoint={truncateYears.includes(record.year)}
                />
              </svg>
              <div className="year-row__hit-area" aria-hidden="true" />
            </div>
          );
        })}
      </div>
      <div className="month-axis" aria-hidden="true">
        {monthMarkers.map(({ month, left, width: monthWidth }) => (
          <span
            key={month}
            style={{ left: `${left}%`, width: `${monthWidth}%` }}
          >
            {month}
          </span>
        ))}
      </div>
    </div>
  );
}

function Multiple({ record, activeYear, truncateYears }) {
  const width = 260;
  const height = 126;
  const active = record.year === activeYear;
  const gradientId = `snow-gradient-${record.year}`;
  const depthMarkers = [0, 100, 200, 300];
  const monthMarkers = [
    ['Mar', 2],
    ['May', 4],
    ['Jul', 6],
    ['Sep', 8],
    ['Nov', 10],
  ];
  return (
    <div className={'multiple' + (active ? ' is-active' : '')}>
      <span>{record.year}</span>
      <svg
        viewBox={'0 0 ' + width + ' 160'}
        role="img"
        aria-label={record.year + ' seasonal snow depth'}
      >
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1={height}
            x2="0"
            y2="0"
          >
            <stop offset="0%" stopColor="#4f6f9f" />
            <stop offset="49%" stopColor="#4f6f9f" />
            <stop offset="60%" stopColor="#765173" />
            <stop offset="76%" stopColor="#d51f36" />
            <stop offset="100%" stopColor="#ed111b" />
          </linearGradient>
        </defs>
        {depthMarkers.map((depth) => {
          const markerY = height - (depth / MAX_DEPTH) * height;
          return (
            <g
              className={
                'multiple__depth-marker' + (depth === 0 ? ' is-baseline' : '')
              }
              key={depth}
            >
              <line x1="0" x2={width} y1={markerY} y2={markerY} />
              <text x="4" y={markerY - 4}>
                {depth === 0 ? '0 (cm)' : depth}
              </text>
            </g>
          );
        })}
        <SnowLine
          record={record}
          width={width}
          height={height}
          className="multiple__line"
          stopAtLastPoint={truncateYears.includes(record.year)}
          stroke={`url(#${gradientId})`}
        />
        {monthMarkers.map(([month, monthIndex]) => (
          <g className="multiple__month-marker" key={month}>
            <line
              x1={(monthIndex / 12) * width}
              x2={(monthIndex / 12) * width}
              y1={height}
              y2={height + 6}
            />
            <text x={((monthIndex + 0.5) / 12) * width} y="151">
              {month}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function Content() {
  const locations = useSelector((state) => state.data.data || {});
  const [location, setLocation] = useState('spencersCreek');
  const data = locations[location] || [];
  const truncateYears = location === 'spencersCreek' ? ['2016', '2026'] : [];
  const activeYear = data.length ? data[data.length - 1].year : '';
  const firstYear = data.length ? data[0].year : '';
  const locationName = LOCATION_NAMES[location];

  if (!data.length)
    return <main className="loading">Loading snow records…</main>;

  return (
    <main className="snow-story">
      <header className="intro">
        <h2 className="chart-title">
          Snow Fall at {locationName}, {firstYear} - {activeYear}
        </h2>
        <p>
          Chart inspired by Joy Division album cover &lsquo;Unknown
          Pleasures&rsquo;, designed by Peter Saville.
        </p>
        <div
          className="location-toggle"
          role="group"
          aria-label="Snow depth location"
        >
          <button
            type="button"
            className={location === 'spencersCreek' ? 'is-active' : ''}
            onClick={() => setLocation('spencersCreek')}
          >
            Spencers Creek
          </button>
          <button
            type="button"
            className={location === 'deepCreek' ? 'is-active' : ''}
            onClick={() => setLocation('deepCreek')}
          >
            Deep Creek
          </button>
          <button
            type="button"
            className={location === 'threeMileDam' ? 'is-active' : ''}
            onClick={() => setLocation('threeMileDam')}
          >
            Three Mile Dam
          </button>
        </div>
      </header>
      <section aria-label="All snow seasons">
        <YearRows
          data={data}
          activeYear={activeYear}
          truncateYears={truncateYears}
        />
      </section>
      <section aria-label="Small multiples">
        <div className="section-heading">
          <p>Individual yearly charts for easier comparison.</p>
        </div>
        <div className="multiple-grid">
          {data.map((record) => (
            <Multiple
              key={record.year}
              record={record}
              activeYear={activeYear}
              truncateYears={truncateYears}
            />
          ))}
        </div>
      </section>
    </main>
  );
}
